import { useState, useCallback, useEffect } from "react";
import {
  BarChart3,
  DollarSign,
  Zap,
  Clock,
  AlertTriangle,
  Loader2,
  Users,
  BrainCircuit,
  Search,
  ScanEye,
  MessageSquare,
  HardDrive,
  TrendingUp,
  Database,
  Hash,
  Activity,
} from "lucide-react";
import { api } from "../../api/client";

function StatCard({ icon: Icon, label, value, sub, color = "stone" }) {
  const colors = {
    stone: "bg-stone-50 border-stone-200 text-stone-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    red: "bg-red-50 border-red-200 text-red-700",
    brand: "bg-brand-red/10 border-brand-red/20 text-brand-red",
  };

  return (
    <div
      className={`rounded-xl border p-4 sm:p-5 ${colors[color] || colors.stone}`}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="size-9 rounded-lg bg-white/60 flex items-center justify-center">
          <Icon size={18} />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wider opacity-70">
          {label}
        </span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && (
        <div className="text-xs opacity-60 mt-1">{sub}</div>
      )}
    </div>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <h3 className="flex items-center gap-2 text-base font-semibold text-stone-800 mb-4">
      <Icon size={18} className="shrink-0 text-stone-500" />
      {children}
    </h3>
  );
}

function ClientRow({ slug, data, isFirst }) {
  const [expanded, setExpanded] = useState(false);

  const typeIcons = {
    chat: MessageSquare,
    embedding: HardDrive,
    ocr: ScanEye,
    "chat-fallback": BrainCircuit,
    "ocr-fallback": ScanEye,
  };

  return (
    <div
      className={`border-stone-100 ${
        isFirst ? "" : "border-t"
      }`}
    >
      <button
        type="button"
        className="w-full flex items-center gap-3 p-3 hover:bg-stone-50 transition-colors text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <span
          className={`transition-transform text-stone-400 ${
            expanded ? "rotate-90" : ""
          }`}
        >
          ▶
        </span>
        <Users size={16} className="text-stone-400 shrink-0" />
        <span className="font-medium text-stone-800 flex-1">{slug}</span>
        <span className="text-xs text-stone-500 bg-stone-100 px-2 py-0.5 rounded">
          {data.count ?? 0} req.
        </span>
        <span className="text-sm font-semibold text-stone-700 w-24 text-right">
          ${(data.cost_usd ?? 0).toFixed(4)}
        </span>
      </button>

      {expanded && (
        <div className="px-6 pb-4 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-stone-50 rounded-lg p-3 text-center">
              <div className="text-xs text-stone-500 uppercase font-semibold">
                Coût
              </div>
              <div className="text-lg font-bold text-stone-800">
                ${(data.cost_usd ?? 0).toFixed(4)}
              </div>
            </div>
            <div className="bg-stone-50 rounded-lg p-3 text-center">
              <div className="text-xs text-stone-500 uppercase font-semibold">
                Latence moy.
              </div>
              <div className="text-lg font-bold text-stone-800">
                {data.avg_latency_ms ?? "—"} ms
              </div>
            </div>
            <div className="bg-stone-50 rounded-lg p-3 text-center">
              <div className="text-xs text-stone-500 uppercase font-semibold">
                Tokens prompt
              </div>
              <div className="text-lg font-bold text-stone-800">
                {(data.prompt_tokens ?? 0).toLocaleString()}
              </div>
            </div>
            <div className="bg-stone-50 rounded-lg p-3 text-center">
              <div className="text-xs text-stone-500 uppercase font-semibold">
                Tokens réponse
              </div>
              <div className="text-lg font-bold text-stone-800">
                {(data.completion_tokens ?? 0).toLocaleString()}
              </div>
            </div>
          </div>

          {Object.keys(data.by_model).length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase text-stone-500 mb-2">
                Par modèle
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(data.by_model).map(([model, m]) => (
                  <div
                    key={model}
                    className="flex items-center justify-between bg-stone-50 rounded-lg px-3 py-2 text-sm"
                  >
                    <span className="text-stone-700 font-mono text-xs truncate">
                      {model}
                    </span>
                    <span className="text-stone-500 text-xs">
                      {m.count ?? 0} req · ${(m.cost_usd ?? 0).toFixed(4)} · {m.avg_latency_ms ?? "—"}ms
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Object.keys(data.by_property_type).length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase text-stone-500 mb-2">
                Par type d'opération
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(data.by_property_type).map(([tname, t]) => {
                  const Icon = typeIcons[tname] || Zap;
                  return (
                    <div
                      key={tname}
                      className="flex items-center gap-2 bg-stone-50 rounded-lg px-3 py-2 text-sm"
                    >
                      <Icon size={14} className="text-stone-400 shrink-0" />
                      <span className="text-stone-700 text-xs capitalize">
                        {tname}
                      </span>
                      <span className="text-stone-500 text-xs ml-auto">
                        {t.count}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function HeliconeMetrics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [days, setDays] = useState(7);
  const [initialLoad, setInitialLoad] = useState(true);

  const fetchMetrics = useCallback(async (force = false) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ days: days.toString() });
      if (force) {
        params.append("force_refresh", "true");
      }
      const res = await api.get(`/api/admin/stats/helicone?${params}`);
      setData(res.data);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message ||
          "Erreur lors du chargement des métriques",
      );
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [days]);

  // Auto-load au montage si données en cache et fraiches
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (initialLoad) {
      fetchMetrics(false);
    }
  }, [fetchMetrics, initialLoad]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const typeIcons = {
    chat: MessageSquare,
    embedding: HardDrive,
    ocr: ScanEye,
    "chat-fallback": BrainCircuit,
    "ocr-fallback": ScanEye,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 size={22} className="text-brand-red" />
          <h2 className="text-xl font-semibold text-stone-800">
            Métriques Helicone
          </h2>
        </div>

        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={(e) => {
              setDays(Number(e.target.value));
              setData(null);
              setInitialLoad(true);
            }}
            className="rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 focus:border-brand-red focus:ring-2 focus:ring-brand-red/20"
          >
            <option value={1}>24 heures</option>
            <option value={7}>7 jours</option>
            <option value={30}>30 jours</option>
            <option value={90}>90 jours</option>
          </select>

          <button
            type="button"
            onClick={() => fetchMetrics(true)}
            disabled={loading}
            className="flex items-center gap-2 rounded-xl bg-stone-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-stone-800 disabled:opacity-50"
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Search size={16} />
            )}
            Charger les métriques
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {data?.error && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700 flex items-center gap-2">
          <AlertTriangle size={16} />
          {data.message || data.error}
        </div>
      )}

      {!data && !loading && !error && (
        <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50/50 p-12 text-center">
          <BarChart3
            size={40}
            className="mx-auto text-stone-300 mb-4"
          />
          <p className="text-stone-500 text-sm">
            Chargement automatique des métriques depuis le cache...
          </p>
          <p className="text-stone-400 text-xs mt-1">
            Les données de moins de 24h sont affichées automatiquement.
          </p>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={32} className="animate-spin text-stone-400" />
          <span className="ml-3 text-stone-500 text-sm">
            Récupération des données Helicone…
          </span>
        </div>
      )}

      {data && !data.error && !loading && (
        <div className="space-y-6">
          {/* Indicateur cache avec ancienneté */}
          {data.from_cache !== undefined && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-stone-500 bg-stone-100 rounded-lg px-3 py-2 w-fit">
                <Database size={14} />
                {data.from_cache ? (
                  <span>Données depuis le cache</span>
                ) : (
                  <span>Données fraiches (API)</span>
                )}
                {data.cache_age_seconds !== undefined && (
                  <span className="text-stone-400">
                    (il y a {Math.round(data.cache_age_seconds / 60)} min)
                  </span>
                )}
              </div>

              {/* Message si données trop anciennes */}
              {data.cache_is_fresh === false && (
                <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <AlertTriangle size={14} />
                  <span>Les données en cache datent de plus de 24h. Cliquez sur "Charger les métriques" pour mettre à jour.</span>
                </div>
              )}
            </div>
          )}

          {/* Vue d'ensemble */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard
              icon={Zap}
              label="Requêtes"
              value={(data.total_requests ?? 0).toLocaleString()}
              sub={`${data.period_days ?? "?"} derniers jours`}
              color="blue"
            />
            <StatCard
              icon={DollarSign}
              label="Coût total"
              value={`$${(data.total_cost_usd ?? 0).toFixed(4)}`}
              color="emerald"
            />
            <StatCard
              icon={AlertTriangle}
              label="Erreurs"
              value={data.errors ?? 0}
              sub={data.error_rate_percent ? `${data.error_rate_percent}%` : null}
              color={(data.errors ?? 0) > 0 ? "red" : "stone"}
            />
            <StatCard
              icon={Activity}
              label="Latence moy."
              value={data.avg_latency_ms ? `${data.avg_latency_ms}ms` : "—"}
              color="blue"
            />
            <StatCard
              icon={Hash}
              label="Tokens total"
              value={(data.total_tokens ?? 0).toLocaleString()}
              color="stone"
            />
            <StatCard
              icon={Users}
              label="Clients actifs"
              value={Object.keys(data.by_client || {}).length}
              color="brand"
            />
          </div>

          {/* Stats supplémentaires: Récupéré le */}
          <div className="flex items-center gap-4 text-xs text-stone-500">
            <div className="flex items-center gap-1">
              <Clock size={14} />
              <span>
                Récupéré le{" "}
                {data.fetched_at
                  ? new Date(data.fetched_at).toLocaleString("fr-FR")
                  : "—"}
              </span>
            </div>
          </div>

          {/* Par type */}
          {Object.keys(data.by_type || {}).length > 0 && (
            <div className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6">
              <SectionTitle icon={Zap}>Répartition par type</SectionTitle>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {Object.entries(data.by_type).map(([tname, t]) => {
                  const Icon = typeIcons[tname] || Zap;
                  return (
                    <div
                      key={tname}
                      className="rounded-xl border border-stone-200 bg-stone-50 p-4"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Icon size={16} className="text-stone-500" />
                        <span className="text-xs font-semibold uppercase text-stone-500">
                          {tname}
                        </span>
                      </div>
                      <div className="text-xl font-bold text-stone-800">
                        {(t.count ?? 0).toLocaleString()}
                      </div>
                      <div className="text-xs text-stone-500 mt-1">
                        ${(t.cost_usd ?? 0).toFixed(4)} ·{" "}
                        {((t.prompt_tokens ?? 0) + (t.completion_tokens ?? 0)).toLocaleString()}{" "}
                        tokens
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Statistiques journalières */}
          {Array.isArray(data.daily_stats) && data.daily_stats.length > 0 && (
            <div className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6">
              <SectionTitle icon={TrendingUp}>Tendances journalières</SectionTitle>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stone-200">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-stone-500 uppercase">Date</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-stone-500 uppercase">Requêtes</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-stone-500 uppercase">Coût</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-stone-500 uppercase">Tokens</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-stone-500 uppercase">Erreurs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.daily_stats.map((day) => (
                      <tr key={day.date} className="border-b border-stone-100 hover:bg-stone-50">
                        <td className="py-2 px-3 font-medium text-stone-700">{day.date}</td>
                        <td className="py-2 px-3 text-right text-stone-600">{day.count?.toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-stone-600">${day.cost_usd?.toFixed(4)}</td>
                        <td className="py-2 px-3 text-right text-stone-600">
                          {((day.prompt_tokens ?? 0) + (day.completion_tokens ?? 0)).toLocaleString()}
                        </td>
                        <td className={`py-2 px-3 text-right ${(day.errors ?? 0) > 0 ? "text-red-600" : "text-stone-400"}`}>
                          {day.errors ?? 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Top modèles */}
          {Array.isArray(data.top_models) && data.top_models.length > 0 && (
            <div className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6">
              <SectionTitle icon={BrainCircuit}>Top modèles (par coût)</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.top_models.map((m, idx) => (
                  <div
                    key={m.model}
                    className="rounded-xl border border-stone-200 bg-stone-50 p-4 flex items-center gap-3"
                  >
                    <div className="size-8 rounded-full bg-stone-200 flex items-center justify-center text-xs font-bold text-stone-600">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-stone-800 truncate" title={m.model}>
                        {m.model}
                      </div>
                      <div className="text-xs text-stone-500">
                        {m.count?.toLocaleString()} req · ${m.cost_usd?.toFixed(4)} · {m.avg_latency_ms}ms
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Par client */}
          {Object.keys(data.by_client || {}).length > 0 && (
            <div className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6">
              <SectionTitle icon={Users}>Par client</SectionTitle>
              <div className="rounded-xl border border-stone-200 overflow-hidden">
                {Object.entries(data.by_client).map(([slug, cdata], idx) => (
                  <ClientRow
                    key={slug}
                    slug={slug}
                    data={cdata}
                    isFirst={idx === 0}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Note info */}
          <div className="text-xs text-stone-400 text-center py-4">
            Données fournies par l'API Helicone Management. Les métriques
            incluent tous les appels LLM, embeddings et OCR transitant par le
            proxy Helicone.
          </div>
        </div>
      )}
    </div>
  );
}
